import {
  Badge,
  Button,
  Checkbox,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  ThrottledButton,
} from "web-design-system";
import {
  useGenerateOtp,
  useSubmitSelfDeclaration,
  webDownload,
} from "creso-core/hooks";
import { Clock, Download, Lock, Loader2 } from "lucide-react";
import { differenceInCalendarDays, format } from "date-fns";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { toast } from "sonner";
import { SuccessLottieAnimation } from "web-core/components";
import { maskEmail } from "creso-core/utils";

type Step = "intro" | "consent" | "otp" | "success";

export interface SelfDeclarationFormProps {
  details: {
    name: string;
    arnCode: string;
    phoneNumber: string;
    email: string;
  };
  defaultStep?: Step;
  /** "sheet-on-mobile" renders OTP in a bottom Sheet on small screens (standalone page).
   *  "inline" always renders OTP inline (inside a Dialog). Defaults to "inline". */
  otpVariant?: "sheet-on-mobile" | "inline";
  /** "submit" runs the full intro → consent → OTP → success flow (default).
   *  "review" opens directly on the consent letter for an already-submitted
   *  declaration, replacing the consent action with download & close. */
  mode?: "submit" | "review";
  /** Link to the already-submitted declaration PDF, used in "review" mode. */
  declarationLink?: string | null;
  onComplete?: () => void;
  onClose?: () => void;
}

const DECLARATION_PARAGRAPHS = [
  "I/we hereby declare that all provisions made under the prescribed code of conduct prescribed by AMFI and the regulatory guidelines as prescribed in clause 15.6 of SEBI's Master circular for Mutual Funds have been adhered to.",
  "In compliance with SEBI's regulatory guidelines, I/we have disclosed to investors all the commissions (in the form of trail commission or any other mode) payable to me/us for the different competing schemes of various mutual funds from amongst which the scheme was recommended to our investors.",
  "I/We also certify that I/we have complied with the Know Your Distributor (KYD) requirements as per AMFI Guidelines.",
  "I/we certify that I/we am/are distributor(s) of mutual funds and registered with AMFI and that in my/our capacity as distributor(s), I/we may provide investment advice to my/our clients, incidental to my/our primary activity of distribution of mutual fund products, which is exempted from registration under Regulation 4(d) of Securities and Exchange Board of India (Investment Advisers) Regulations, 2013.",
  "I/We further certify that I/we have read, understood and adhered to the SEBI and AMFI Guidelines issued from time to time.",
];

export const SIGNUP_DATE_CUTOFF = new Date(2026, 2, 31); // 31 Mar 2026
export const DEADLINE = new Date(2026, 8, 30); // 30 Sept 2026

function getDaysRemaining() {
  return Math.max(0, differenceInCalendarDays(DEADLINE, new Date()));
}

export function SelfDeclarationForm({
  details,
  otpVariant = "inline",
  mode = "submit",
  declarationLink,
  onComplete,
  onClose,
  defaultStep,
}: SelfDeclarationFormProps) {
  const { name, phoneNumber, email, arnCode } = details;
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const useSheet = otpVariant === "sheet-on-mobile" && !isDesktop;

  const [step, setStep] = useState<Step>(
    defaultStep ?? (mode === "review" ? "consent" : "intro")
  );
  const [consentChecked, setConsentChecked] = useState(false);
  const [otp, setOtp] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  const { mutateAsync: generateOtp, isPending: isGeneratingOtp } =
    useGenerateOtp();
  const { mutateAsync: submitDeclaration, isPending: isSubmitting } =
    useSubmitSelfDeclaration();

  const maskedPhone = phoneNumber ? `+91****${phoneNumber.slice(-4)}` : "";
  const maskedEmail = maskEmail(email);

  const handleProceed = async () => {
    try {
      await generateOtp({ arn_code: arnCode });
      setStep("otp");
    } catch {
      toast.error("Failed to send OTP. Please try again.");
    }
  };

  const handleOtpSubmit = async () => {
    try {
      const result = await submitDeclaration({ arnCode, otp });
      setPdfUrl(result.declaration_link);
      setStep("success");
      onComplete?.();
    } catch {
      toast.error("Verification failed. Please check the OTP and try again.");
    }
  };

  const handleResendOtp = async () => {
    try {
      await generateOtp({ arn_code: arnCode });
      toast.success("OTP resent successfully.");
    } catch {
      toast.error("Failed to resend OTP.");
    }
  };

  const otpBodyProps: OtpBodyProps = {
    otp,
    onOtpChange: setOtp,
    onSubmit: handleOtpSubmit,
    onResend: handleResendOtp,
    isSubmitting,
  };

  return (
    <>
      {step === "intro" && (
        <IntroStep
          name={name}
          arnCode={arnCode}
          onNext={() => setStep("consent")}
        />
      )}

      {/* Consent stays visible on mobile while Sheet is open */}
      {(step === "consent" || (step === "otp" && useSheet)) && (
        <ConsentStep
          name={name}
          arnCode={arnCode}
          phoneNumber={phoneNumber}
          consentChecked={consentChecked}
          onConsentChange={setConsentChecked}
          onProceed={handleProceed}
          isLoading={isGeneratingOtp}
          mode={mode}
          declarationLink={declarationLink}
          onClose={onClose}
        />
      )}

      {/* OTP — inline for dialog and desktop */}
      {step === "otp" && !useSheet && (
        <OtpStep
          email={maskedEmail}
          maskedPhone={maskedPhone}
          {...otpBodyProps}
        />
      )}

      {step === "success" && <SuccessStep pdfUrl={pdfUrl} />}

      {/* OTP — bottom Sheet for mobile standalone page */}
      {otpVariant === "sheet-on-mobile" && (
        <Sheet
          open={step === "otp" && useSheet}
          onOpenChange={(open) => {
            if (!open) {
              setStep("consent");
              setOtp("");
            }
          }}
        >
          <SheetContent side="bottom" className="px-6 pb-8 rounded-t-2xl">
            <SheetHeader className="text-left mb-2">
              <SheetTitle className="text-lg">Verify Your Identity</SheetTitle>
              <SheetDescription>
                Enter the 6-digit OTP sent to&nbsp;
                <span className="font-medium text-foreground break-all">
                  {maskedPhone} or {maskedEmail}
                </span>
              </SheetDescription>
            </SheetHeader>
            <OtpBody {...otpBodyProps} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-4 first:pt-2 last:pb-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

function IntroStep({
  name,
  arnCode,
  onNext,
}: {
  name: string;
  arnCode: string;
  onNext: () => void;
}) {
  const daysRemaining = getDaysRemaining();

  return (
    <div className="flex flex-col flex-1 gap-4">
      {/* Header card */}
      <div className="rounded-lg flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-xl font-semibold">
            Declaration of Self Certification
          </span>
          <span className="text-sm text-muted-foreground">
            Financial Year 2025-2026. Please review and submit your AMFI
            self-certification declaration.
          </span>
        </div>
      </div>

      {/* ARN Holder card */}
      <div className="rounded-lg mb-6 mt-2 border border-border p-4 flex flex-col gap-3">
        <span className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          ARN HOLDER
        </span>
        <div className="flex flex-col divide-y divide-border">
          <InfoRow label="Name" value={name} />
          <InfoRow label="ARN" value={arnCode} />
          <InfoRow label="Period" value="01/04/2025 – 31/03/2026" />
          <InfoRow label="Financial Year" value="2025-2026" />
        </div>
      </div>

      {/* Deadline warning */}
      <div className="flex flex-col mt-auto gap-3 pb-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/50 p-4 flex gap-3 items-start">
          <Clock className="size-4 text-amber-700 dark:text-amber-300 shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Submit by {format(DEADLINE, "PPP")}
              {" · "}
              <span className="font-semibold">
                {daysRemaining} days remaining.
              </span>
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Failure to submit will result in withholding of your commissions.
            </p>
          </div>
        </div>

        <Button size="lg" className="w-full" onClick={onNext}>
          Review & Submit Declaration
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <Lock className="size-3" />
          Verified via OTP to your registered mobile
        </p>
      </div>
    </div>
  );
}

function ConsentStep({
  name,
  arnCode,
  phoneNumber,
  consentChecked,
  onConsentChange,
  onProceed,
  isLoading,
  mode = "submit",
  declarationLink,
  onClose,
}: {
  name: string;
  arnCode: string;
  phoneNumber: string;
  consentChecked: boolean;
  onConsentChange: (v: boolean) => void;
  onProceed: () => void;
  isLoading: boolean;
  mode?: "submit" | "review";
  declarationLink?: string | null;
  onClose?: () => void;
}) {
  const isReview = mode === "review";
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="flex flex-col flex-1">
      {/* Scrollable content — pb keeps last line clear of the sticky bar */}
      <div className="flex flex-col gap-5 pb-10">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-xl font-semibold">Declaration Letter</span>
            <span className="text-sm text-muted-foreground">
              Financial Year 2025-2026
            </span>
          </div>
          {isReview && declarationLink ? (
            <Badge
              variant="custom"
              className="bg-tag-green text-tag-green-foreground gap-1 flex items-center"
            >
              Submitted
            </Badge>
          ) : null}
        </div>

        {/* From / To card */}
        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">
            {isReview && declarationLink ? (
              `Submitted on ${getSubmittedDate(declarationLink)}`
            ) : (
              <>
                Date:&nbsp;<span className="font-semibold">{today}</span>
              </>
            )}
          </span>
          <div className="rounded-xl bg-muted/40 border border-border p-4 text-[13px]">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                From
              </p>
              <div className="space-y-2 text-foreground/80">
                <p>
                  <span className="text-muted-foreground">Name:</span>{" "}
                  <span className="font-medium text-foreground">{name}</span>
                </p>
                <p>
                  <span className="text-muted-foreground">ARN:</span>{" "}
                  <span className="font-medium text-foreground">{arnCode}</span>
                </p>
                <p>
                  <span className="text-muted-foreground">Mobile:</span>{" "}
                  <span className="font-medium text-foreground">
                    {phoneNumber}
                  </span>
                </p>
              </div>
            </div>
            <div className="border-t border-border mt-4 pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                To
              </p>
              <div className="space-y-2 text-foreground/80">
                <p>
                  <span className="font-medium text-foreground">
                    Creso Technologies Private Limited
                  </span>
                </p>
                <p>
                  <span className="text-muted-foreground">ARN:</span>{" "}
                  <span className="font-medium text-foreground">321367</span>
                </p>
                <p>
                  <span className="text-muted-foreground">Address:</span>{" "}
                  <span className="font-medium text-foreground">
                    Mumbai, Maharashtra 400063
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Declaration — 5 paragraphs */}
        <div className="text-xs leading-relaxed text-foreground/80 space-y-3">
          {DECLARATION_PARAGRAPHS.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

        {isReview && (
          <div className="rounded-lg bg-tag-green p-3">
            <p className="text-sm text-tag-green-foreground">
              Consent recorded: the ARN holder has read and accepted this
              declaration, verified via OTP
              {declarationLink
                ? ` on ${getSubmittedDate(declarationLink)}`
                : ""}
              .
            </p>
          </div>
        )}
      </div>

      {/* Docked bottom — sticks to bottom of scroll viewport */}
      <div className="sticky bottom-0 pb-6 bg-background border-t border-border -mx-6 px-6 pt-4 space-y-3">
        {isReview ? (
          <div className="space-y-2">
            <Button
              size="lg"
              className="w-full gap-2"
              disabled={!declarationLink}
              onClick={async () => {
                if (!declarationLink) return;
                try {
                  const response = await fetch(declarationLink);
                  const blob = await response.blob();
                  webDownload(blob, "self-declaration.pdf");
                } catch {
                  toast.error("Failed to download declaration PDF");
                }
              }}
            >
              <Download className="size-4" />
              Download Declaration PDF
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              Close
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              This declaration has been submitted and registered with AMFI. Keep
              a copy for your records.
            </p>
          </div>
        ) : (
          <>
            <label className="flex items-start gap-3 cursor-pointer group">
              <Checkbox
                checked={consentChecked}
                onCheckedChange={(v) => onConsentChange(v === true)}
                className="mt-0.5"
              />
              <p className="text-xs text-foreground/80 group-hover:text-foreground transition-colors">
                I have read and understood the above declaration. I confirm that
                the above statements are true and correct to the best of my
                knowledge and belief.
              </p>
            </label>
            <div className="space-y-2">
              <Button
                size="lg"
                className="w-full"
                disabled={!consentChecked || isLoading}
                onClick={onProceed}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Sending OTP…
                  </>
                ) : (
                  "Proceed to Verify"
                )}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                An OTP will be sent to your registered mobile number to
                authenticate this declaration.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type OtpBodyProps = {
  otp: string;
  onOtpChange: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  isSubmitting: boolean;
};

function OtpBody({
  otp,
  onOtpChange,
  onSubmit,
  onResend,
  isSubmitting,
}: OtpBodyProps) {
  return (
    <div className="flex flex-col gap-8 flex-1 mt-4">
      <div className="flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={onOtpChange}>
          <InputOTPGroup className="gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot
                key={i}
                index={i}
                className="size-11 text-base rounded-lg border-border"
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      <div className="flex flex-col mt-auto pb-6 gap-3">
        <Button
          size="lg"
          className="w-full"
          disabled={otp.length < 6 || isSubmitting}
          onClick={onSubmit}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Submitting…
            </>
          ) : (
            "Submit Declaration"
          )}
        </Button>

        <ThrottledButton
          throttle={30}
          beginOnMount
          variant="ghost"
          size="lg"
          className="w-full"
          onClick={onResend}
        >
          Resend OTP
        </ThrottledButton>
        <p className="text-center text-xs text-muted-foreground">
          By submitting, you authenticate this declaration with your registered
          mobile number.
        </p>
      </div>
    </div>
  );
}

function OtpStep({
  maskedPhone,
  email,
  ...props
}: { maskedPhone: string; email: string } & OtpBodyProps) {
  return (
    <div className="flex flex-col flex-1 gap-4">
      <div className="flex flex-col gap-1.5">
        <span className="text-xl font-semibold">Verify Your Identity</span>
        <p className="text-sm text-muted-foreground break-all">
          Enter the 6-digit OTP sent to{" "}
          <span className="font-medium text-foreground">
            {maskedPhone} or {email}
          </span>
        </p>
      </div>
      <OtpBody {...props} />
    </div>
  );
}

/**
 * Submission date is encoded as the trailing `YYYY-MM-DD` of the declaration
 * PDF file name, e.g. `777777_declaration_2026-2027_2026-06-06.pdf`.
 * Falls back to today's date if the link can't be parsed.
 */
function getSubmittedDate(pdfUrl: string) {
  const match = pdfUrl.match(/(\d{4})-(\d{2})-(\d{2})(?=\.pdf)/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }
  return new Date().toLocaleDateString("en-GB");
}

function SuccessStep({ pdfUrl }: { pdfUrl: string }) {
  const today = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const submittedDate = getSubmittedDate(pdfUrl);
  return (
    <div className="flex flex-col flex-1 items-center gap-8 py-4">
      <div className="flex flex-col items-center">
        <SuccessLottieAnimation loop={false} height={150} width={150} />
        <div className="flex flex-col items-center gap-1.5 text-center">
          <span className="text-lg font-semibold tracking-tight">
            Declaration Submitted
          </span>
          <span className="text-sm text-muted-foreground max-w-md">
            Your FY 2025-2026 self-certification has been recorded on&nbsp;
            {today}. A confirmation has been sent to your registered email.
          </span>
        </div>
      </div>

      <div className="w-full rounded-lg border border-border bg-card px-4 flex flex-col divide-y divide-border">
        <div className="flex items-center justify-between py-3">
          <span className="text-sm text-muted-foreground">Submitted on</span>
          <span className="text-sm font-bold">{submittedDate}</span>
        </div>
        <div className="flex items-center justify-between py-3">
          <span className="text-sm text-muted-foreground">Status</span>
          <span className="flex items-center gap-1.5 text-sm font-bold text-constructive">
            <span className="size-1.5 rounded-full bg-constructive" />
            Submitted
          </span>
        </div>
      </div>

      {pdfUrl ? (
        <div className="w-full mt-auto gap-3 pb-6 flex flex-col">
          <Button
            size="lg"
            variant="outline"
            className="w-full gap-2"
            onClick={async () => {
              if (!pdfUrl) return;
              try {
                const response = await fetch(pdfUrl);
                const blob = await response.blob();
                webDownload(blob, "self-declaration.pdf");
              } catch {
                toast.error("Failed to download declaration PDF");
              }
            }}
          >
            <Download className="size-4" />
            Download Declaration PDF
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Keep this document for your records.
          </p>
        </div>
      ) : null}
    </div>
  );
}
